import type { KeyboardEvent } from "react";
import { Info, Plus } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { TargetArrow } from "@/components/ui/target-arrow-icon";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CategoryNamesFit } from "./CategoryNamesFit";
import { leafName } from "./categoryLabel";

export interface BrandCategoryBadgeProps {
  /** The effective choice's categories, in FR-1 order. */
  categories: { leaf_category_id: string; name: string }[];
  readOnly: boolean;
  onEdit: () => void;
  /** Receives the focusable element, so the edit dialog can return focus to it on close. */
  focusRef?: (el: HTMLElement | null) => void;
}

export function BrandCategoryBadge({ categories, readOnly, onEdit, focusRef }: BrandCategoryBadgeProps) {
  if (categories.length === 0) {
    if (readOnly) return null;
    return (
      <button
        ref={focusRef}
        type="button"
        onClick={onEdit}
        data-testid="brand-category-select-button"
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-dashed border-muted-foreground/50 bg-muted/50 px-2.5 py-0.5 text-xs font-medium text-foreground hover:border-primary hover:bg-primary-tint-soft focus-visible:ring-2"
      >
        <Plus className="h-3 w-3" aria-hidden />
        Select a primary category
      </button>
    );
  }

  const names = categories.map((c) => c.name);
  const isNiche = categories.length > 1;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onEdit();
    }
  };

  const interactiveProps = readOnly
    ? {}
    : { interactive: true, role: "button", tabIndex: 0, "aria-haspopup": "dialog" as const, onClick: onEdit, onKeyDown };

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            ref={focusRef}
            variant="primary"
            className="min-w-0 max-w-full gap-1 whitespace-nowrap"
            data-testid="brand-category-badge"
            aria-label={`Primary category or niche: ${names.map(leafName).join(", ")}`}
            {...interactiveProps}
          >
            <TargetArrow className="h-3 w-3 shrink-0" strokeWidth={2} />
            <CategoryNamesFit names={names} />
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-semibold">Primary Category or Niche</p>
          <p className="mt-1">
            {isNiche
              ? "Market data for share of voice, competitors and trends comes from these categories."
              : "Market data for share of voice, competitors and trends comes from this category."}
          </p>
          <Badge variant="primary" className="mt-2 items-start gap-1 rounded whitespace-normal" data-testid="brand-category-card-badge">
            <TargetArrow className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={2} />
            <span className="max-h-64 overflow-y-auto" data-testid="brand-category-card-names">
              {names.map(leafName).join(", ")}
            </span>
          </Badge>
          {isNiche && (
            <Alert variant="info" className="mt-2 text-xs">
              <Info className="h-4 w-4" aria-hidden />
              <AlertDescription className="text-xs">
                The data is blended across the selected categories.
              </AlertDescription>
            </Alert>
          )}
          {!readOnly && (
            <div className="mt-2 flex justify-end border-t border-border pt-2">
              <button type="button" onClick={onEdit} className="font-medium text-primary hover:underline">
                Change category or niche
              </button>
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
